import React, { useState, useEffect, useCallback } from 'react';
import { 
  Paperclip, 
  Download, 
  Trash2, 
  Plus,
  FileText,
  Image,
  Video,
  Music,
  Archive,
  Code,
  AlertCircle
} from 'lucide-react';
import attachmentService from '../services/attachmentService';
import AttachmentUploader from './AttachmentUploader';

const AttachmentList = ({ 
  questionId, 
  userId = 'current_user',
  canUpload = true,
  canDelete = true,
  showUploadButton = true 
}) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUploader, setShowUploader] = useState(false);
  const [deleting, setDeleting] = useState({});

  const loadAttachments = useCallback(async () => {
    try {
      setLoading(true);
      const result = await attachmentService.getQuestionAttachments(questionId);
      if (result.success) {
        console.log('Loaded attachments:', result.data);
        setAttachments(result.data);
      } else {
        setError('Failed to load attachments');
      }
    } catch (error) {
      console.error('Error loading attachments:', error);
      setError('Failed to load attachments');
    } finally {
      setLoading(false);
    }
  }, [questionId]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const handleUploadComplete = (newAttachments) => {
    setAttachments(prev => [...prev, ...newAttachments]);
    setShowUploader(false);
  };

  const handleDownload = async (attachment) => {
    try {
      await attachmentService.downloadAttachment(attachment);
    } catch (error) {
      console.error('Download failed:', error);
      // You could show a toast notification here
    }
  };

  const handleDelete = async (attachmentId) => {
    if (!canDelete) return;

    try {
      setDeleting(prev => ({ ...prev, [attachmentId]: true }));
      
      const result = await attachmentService.deleteAttachment(attachmentId);
      if (result.success) {
        setAttachments(prev => prev.filter(att => att.id !== attachmentId));
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      // You could show a toast notification here
    } finally {
      setDeleting(prev => ({ ...prev, [attachmentId]: false }));
    }
  };

  const getFileIcon = (fileType) => {
    const iconClass = attachmentService.getFileIconClass(fileType);
    
    switch (fileType) {
      case 'image': return <Image className={`h-5 w-5 ${iconClass}`} />;
      case 'pdf': return <FileText className={`h-5 w-5 ${iconClass}`} />;
      case 'video': return <Video className={`h-5 w-5 ${iconClass}`} />;
      case 'audio': return <Music className={`h-5 w-5 ${iconClass}`} />;
      case 'archive': return <Archive className={`h-5 w-5 ${iconClass}`} />;
      case 'code': return <Code className={`h-5 w-5 ${iconClass}`} />;
      default: return <Paperclip className={`h-5 w-5 ${iconClass}`} />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-sm text-gray-600">Loading attachments...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Paperclip className="h-5 w-5 text-gray-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">
            Attachments ({attachments.length})
          </h3>
        </div>
        
        {showUploadButton && canUpload && (
          <button
            onClick={() => setShowUploader(true)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Files
          </button>
        )}
      </div>

      {/* Attachments List */}
      {attachments.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
          <Paperclip className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-sm font-medium text-gray-900 mb-2">No attachments</h3>
          <p className="text-sm text-gray-500 mb-4">
            {canUpload ? 'Upload files to share with your team' : 'No files have been shared yet'}
          </p>
          {canUpload && showUploadButton && (
            <button
              onClick={() => setShowUploader(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Upload Files
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {attachments.map((attachment) => (
            <div
              key={attachment.id}
              className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
            >
              <div className="flex items-center flex-1 min-w-0">
                <div className="flex-shrink-0 mr-3">
                  {getFileIcon(attachment.file_type)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {attachment.filename}
                  </p>
                  <div className="flex items-center space-x-4 mt-1">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${attachmentService.getFileTypeBadgeColor(attachment.file_type)}`}>
                      {attachment.file_type}
                    </span>
                    <span className="text-xs text-gray-500">
                      {attachmentService.formatFileSize(attachment.file_size)}
                    </span>
                    <span className="text-xs text-gray-500">
                      by {attachment.uploader_name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(attachment.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => handleDownload(attachment)}
                  className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Download"
                >
                  <Download className="h-4 w-4" />
                </button>
                
                {canDelete && (
                  <button
                    onClick={() => handleDelete(attachment.id)}
                    disabled={deleting[attachment.id]}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Delete"
                  >
                    {deleting[attachment.id] ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploader && (
        <AttachmentUploader
          questionId={questionId}
          userId={userId}
          onAttachmentUploaded={handleUploadComplete}
          onClose={() => setShowUploader(false)}
        />
      )}
    </div>
  );
};

export default AttachmentList;
